import { getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { IPaginatedResponse, ISupplierListItem } from 'pages/supplier-intelligence/utils/types';
import type { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/supplier-intelligence/suppliers';
export const getSiSuppliersEndpointIdentifier = endpoint;

type TData = IPaginatedResponse<ISupplierListItem>;
type TError = AxiosError;
type TQueryKey = [typeof endpoint, { page: TParam; limit: TParam; search: TParam; sort: TParam; isActive: TParam; isPublic: TParam }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetSiSuppliersApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = 10;
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { search, sort, isActive, isPublic } = getPageParams();

  const queryFn = async () => {
    try {
      const params: any = { page, limit };
      if (search) params.search = search;
      if (sort) params.sort = sort;
      if (isActive) params.isActive = isActive;
      if (isPublic !== undefined && isPublic !== null) params.isPublic = isPublic;
      const { data } = await http.get(endpoint, { params });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint, { page, limit, search, sort: sort ?? null, isActive: isActive ?? null, isPublic: isPublic ?? null }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
